import {
  TableChart as CsvIcon,
  InsertDriveFile as DefaultIcon,
  Description as DocIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { Stack, styled, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { localeFormatterHelper } from '../../../helpers/formatter/localeFormatter.helper';
import { AIAssistanRes } from '../service/aiAssistant.service';

const SAFETY_BUFFER_MS = 60_000;
const DEFAULT_FALLBACK_AVAILABLE_MS = 600_000;

export const File: FC<{ file: AIAssistanRes['reply']['content']['files'][number] }> = ({
  file,
}) => {
  const { name, expiresAt } = useMemo(() => extractFileInfo(file!.path), [file?.path]);

  const effectiveExpiresAt = useMemo(
    () => new Date(expiresAt.getTime() - SAFETY_BUFFER_MS),
    [expiresAt],
  );

  const [now, setNow] = useState<number>(Date.now());
  const remainingMs = Math.max(0, effectiveExpiresAt.getTime() - now);
  const isExpired = remainingMs <= 0;

  useEffect(() => {
    if (isExpired) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isExpired]);

  const { t } = useTranslation();

  return (
    <Wrapper>
      <ResetLink
        href={isExpired ? undefined : file?.path}
        target={isExpired ? (undefined as any) : '_blank'}
        rel={isExpired ? (undefined as any) : 'noopener noreferrer'}
        style={{
          cursor: isExpired ? 'not-allowed' : 'pointer',
          opacity: isExpired ? 0.5 : 1,
          pointerEvents: isExpired ? 'none' : undefined,
        }}
        aria-disabled={isExpired}
      >
        <Stack
          direction='row'
          sx={{
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <DocumentIcon
            height='24px'
            width='24px'
            color={'rgba(244, 140, 7, 1)'}
            extension={file!.file_format}
          />
          <Typography
            variant='body2'
            title={name}
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              display: 'inline-block',
              maxWidth: '100%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              direction: 'rtl',
              textAlign: 'left',
            }}
          >
            {name}
          </Typography>
        </Stack>

        <Typography
          variant='body2'
          sx={{
            fontSize: '10px',
          }}
        >
          {isExpired
            ? t('aiAssistant.downloadExpired')
            : `${t('aiAssistant.downloadAvaliable')} ${localeFormatterHelper.formatDate(
                effectiveExpiresAt,
                { dateStyle: 'short', timeStyle: 'short' },
                true,
              )}`}
        </Typography>
        <Typography
          variant='body2'
          sx={{
            fontSize: '10px',
          }}
        >
          {!isExpired && t('aiAssistant.timeLeft') + ': ' + formatCountdown(remainingMs)}
        </Typography>
      </ResetLink>
    </Wrapper>
  );
};

const Wrapper = styled('div')(() => ({
  marginBottom: '16px',
  padding: '8px 16px',
  borderRadius: '10px',
  backgroundColor: 'rgba(244, 140, 7, 0.12)',
}));

const ResetLink = styled('a')`
  text-decoration: none;
  color: inherit;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  &:hover,
  &:focus,
  &:active {
    text-decoration: none;
    color: inherit;
    outline: none;
  }
`;

function extractFileInfo(url: string): { name: string; expiresAt: Date } {
  const urlObj = new URL(url);
  const pathSegments = urlObj.pathname.split('/');
  const fileName = pathSegments[pathSegments.length - 1].split('?')[0];
  const absExpires = urlObj.searchParams.get('Expires');
  let expiresAt: Date;

  if (absExpires) {
    expiresAt = new Date(parseInt(absExpires, 10) * 1000);
  } else {
    expiresAt = new Date(Date.now() + DEFAULT_FALLBACK_AVAILABLE_MS + SAFETY_BUFFER_MS);
  }

  return { name: fileName, expiresAt };
}

function formatCountdown(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

interface DocumentIconProps {
  extension: string;
  [key: string]: any;
}

const DocumentIcon = ({ extension, ...props }: DocumentIconProps) => {
  const getIcon = (ext: string) => {
    switch (ext?.toLowerCase()) {
      case '.doc':
      case '.docx':
        return <DocIcon style={{ ...props }} />;
      case '.pdf':
        return <PdfIcon style={{ ...props }} />;
      case '.csv':
        return <CsvIcon style={{ ...props }} />;
      case '.txt':
      default:
        return <DefaultIcon style={{ ...props }} />;
    }
  };

  return getIcon(extension);
};
